"use client"

import { useEffect, useRef, useState } from "react"
import { useParams } from "next/navigation"
import { useAiChat, normalizeLocale } from "@lib/hooks/use-ai-chat"
import { AiContent } from "@lib/util/ai-content"

const PROMPT_CHIPS_UA = [
  "Підбери MOSFET для ШІМ 24В 5А",
  "Аналог транзистора BC547",
  "Конденсатор для фільтра БП 12В 2А",
]
const PROMPT_CHIPS_RU = [
  "Подбери MOSFET для ШИМ 24В 5А",
  "Аналог транзистора BC547",
  "Конденсатор для фильтра БП 12В 2А",
]
const PROMPT_CHIPS_EN = [
  "Pick a MOSFET for 24V 5A PWM",
  "BC547 transistor analog",
  "Filter capacitor for 12V 2A PSU",
]

const TXT = {
  ua: {
    title: "AI-помічник Kvatronix",
    online: "Онлайн",
    placeholder: "Опишіть що вам потрібно...",
    send: "Надіслати",
    close: "Закрити",
    intro:
      "Опишіть задачу — підкажу компонент із каталогу, аналог або параметри.",
    sending: "Надсилаю...",
    rateLimit: (s: number) => `Перевищено ліміт. Зачекайте ${s}с`,
  },
  ru: {
    title: "AI-помощник Kvatronix",
    online: "Онлайн",
    placeholder: "Опишите, что вам нужно...",
    send: "Отправить",
    close: "Закрыть",
    intro:
      "Опишите задачу — подскажу компонент из каталога, аналог или параметры.",
    sending: "Отправляю...",
    rateLimit: (s: number) => `Превышен лимит. Подождите ${s}с`,
  },
  en: {
    title: "Kvatronix AI assistant",
    online: "Online",
    placeholder: "Describe what you need...",
    send: "Send",
    close: "Close",
    intro: "Describe your task — I'll suggest a component, analog or specs.",
    sending: "Sending...",
    rateLimit: (s: number) => `Rate limit. Wait ${s}s`,
  },
} as const

export default function AiChatFab() {
  const params = useParams()
  const locale = normalizeLocale(params?.countryCode as string | undefined)
  const t = TXT[locale]
  const chips =
    locale === "ru"
      ? PROMPT_CHIPS_RU
      : locale === "en"
        ? PROMPT_CHIPS_EN
        : PROMPT_CHIPS_UA

  const [open, setOpen] = useState(false)
  const [input, setInput] = useState("")
  const { messages, loading, error, retryAfter, send } = useAiChat({ locale })
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [messages, loading])

  useEffect(() => {
    if (typeof window === "undefined") return
    const handler = () => setOpen(true)
    window.addEventListener("kvatronix:open-ai-chat", handler)
    return () => window.removeEventListener("kvatronix:open-ai-chat", handler)
  }, [])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = input
    setInput("")
    await send(text)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      const form = (e.target as HTMLTextAreaElement).form
      form?.requestSubmit()
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r from-brand-light to-brand-600 text-2xl text-white shadow-lg shadow-brand-light/25 transition hover:scale-110 hover:shadow-brand-light/35 dark:from-brand-dark dark:to-brand-500 dark:shadow-brand-dark/20"
        aria-label={t.title}
        title={t.title}
      >
        {open ? "×" : "🤖"}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t.title}
          className="fixed inset-0 z-[60] flex items-end justify-end bg-black/30 backdrop-blur-sm md:bottom-24 md:right-5 md:inset-auto md:bg-transparent md:backdrop-blur-0"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false)
          }}
        >
          <div className="flex h-full w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200/60 bg-white shadow-2xl md:h-[600px] md:max-h-[80vh] md:w-[400px] md:rounded-2xl dark:border-white/8 dark:bg-[#0a0e1a]">
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3 dark:border-white/5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-light/10 dark:bg-brand-dark/10">
                <img src="/logo-mark.svg" alt="" className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                  {t.title}
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {t.online}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t.close}
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 dark:hover:bg-white/5"
              >
                ×
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
            >
              {messages.length === 0 && (
                <>
                  <div className="rounded-2xl bg-slate-100 px-4 py-3 text-[13px] leading-relaxed dark:bg-[#1e293b]">
                    {t.intro}
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {chips.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setInput(c)}
                        className="rounded-full border border-brand-light/25 bg-brand-light/5 px-3 py-1.5 text-[12px] text-brand-light transition hover:bg-brand-light/10 dark:border-brand-dark/20 dark:bg-brand-dark/8 dark:text-brand-dark dark:hover:bg-brand-dark/12"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {messages.map((m, i) => (
                <div
                  key={i}
                  className={
                    m.role === "user"
                      ? "max-w-[85%] self-end ml-auto rounded-2xl rounded-br-sm border border-brand-light/15 bg-brand-light/5 px-4 py-2.5 text-[13px] leading-relaxed dark:border-brand-dark/15 dark:bg-brand-dark/8"
                      : "max-w-[90%] self-start rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap dark:bg-[#1e293b]"
                  }
                >
                  {m.role === "assistant" ? <AiContent text={m.content} /> : m.content}
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-1.5 self-start rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 dark:bg-[#1e293b]">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                </div>
              )}
            </div>

            {/* Error / rate-limit banner */}
            {(error || retryAfter > 0) && (
              <div className="border-t border-amber-200/60 bg-amber-50 px-4 py-2 text-[12px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                {retryAfter > 0 ? t.rateLimit(retryAfter) : error}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={onSubmit}
              className="flex gap-2 border-t border-slate-100 p-3 dark:border-white/5"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={t.placeholder}
                disabled={loading || retryAfter > 0}
                className="flex-1 resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-light disabled:opacity-50 dark:border-white/10 dark:bg-[#111827] dark:text-white dark:focus:border-brand-dark"
              />
              <button
                type="submit"
                disabled={!input.trim() || loading || retryAfter > 0}
                aria-label={t.send}
                className="flex h-9 w-9 shrink-0 items-center justify-center self-end rounded-lg bg-gradient-to-r from-brand-light to-brand-600 text-white transition hover:opacity-90 disabled:opacity-40 dark:from-brand-dark dark:to-brand-500"
              >
                ↑
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
